import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, AI_CATEGORIES } from "../lib/api";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Mail, MailOpen, EyeOff, Clock, RefreshCw, Sparkles, ArrowRight, Inbox,
} from "lucide-react";

export default function Dashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [emails, setEmails] = useState([]);
  const [hasAccount, setHasAccount] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    try {
      const accs = await api.get("/email-accounts");
      setHasAccount(accs.data.length > 0);
      const [s, e] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/emails", { params: { scope: "priority", limit: 8 } }),
      ]);
      setStats(s.data);
      setEmails(e.data);
    } catch (err) { /* noop */ }
  };

  useEffect(() => { load(); }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/emails/sync", null, { params: { limit: 50 } });
      toast.success(`Sync : ${r.data.new} nouveaux, ${r.data.classified} classifiés par IA`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la synchronisation");
    } finally { setSyncing(false); }
  };

  const generateDigest = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/summaries/generate", null, { params: { digest_type: "custom", send: false } });
      toast.success(`Résumé généré (${r.data.email_count} emails)`);
      nav("/summaries");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec génération");
    } finally { setGenerating(false); }
  };

  if (hasAccount === false) {
    return (
      <div className="p-10">
        <div className="max-w-2xl bg-white border border-gray-200 p-10">
          <div className="overline mb-3">// Première étape</div>
          <h1 className="text-3xl font-bold tracking-tight mb-3" style={{fontFamily:"'Work Sans', sans-serif"}}>
            Connectez votre boîte LWS
          </h1>
          <p className="text-gray-600 mb-6">Pour commencer, configurez vos identifiants IMAP/SMTP. Vos credentials sont chiffrés (Fernet AES-128).</p>
          <button onClick={() => nav("/account")} data-testid="setup-account-btn"
            className="bg-[#002FA7] hover:bg-[#002277] text-white px-6 py-3 font-medium transition-colors">
            Configurer le compte email →
          </button>
        </div>
      </div>
    );
  }

  const nextDigest = stats?.next_digest ? new Date(stats.next_digest) : null;

  return (
    <div className="p-6 md:p-8" data-testid="dashboard-page">
      {/* Topbar */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="overline mb-1">// Dashboard</div>
          <h1 className="text-3xl font-bold tracking-tight" style={{fontFamily:"'Work Sans', sans-serif"}}>
            Centre de contrôle
          </h1>
        </div>
        <div className="flex gap-3">
          <button onClick={sync} disabled={syncing} data-testid="sync-btn"
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 hover:border-gray-900 text-sm font-medium transition-colors disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Sync…" : "Synchroniser"}
          </button>
          <button onClick={generateDigest} disabled={generating} data-testid="generate-digest-btn"
            className="flex items-center gap-2 px-4 py-2.5 bg-[#002FA7] hover:bg-[#002277] text-white text-sm font-medium transition-colors disabled:opacity-50">
            <Sparkles className="w-4 h-4" />
            {generating ? "Génération…" : "Générer résumé IA"}
          </button>
        </div>
      </div>

      {/* Widgets */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="stat-priority" label="Prioritaires non lus" value={stats?.priority_unread ?? "—"} icon={Mail} accent />
        <StatCard testid="stat-read" label="Emails lus" value={stats?.read_count ?? "—"} icon={MailOpen} />
        <StatCard testid="stat-hidden" label="Emails ignorés" value={stats?.hidden_count ?? "—"} icon={EyeOff} />
        <StatCard testid="stat-next-digest" label="Prochain résumé" value={nextDigest ? nextDigest.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : "—"} icon={Clock} />
      </div>

      {/* Chart + Categories */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 bg-white border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
            <div>
              <div className="overline mb-1">// Volume</div>
              <h3 className="text-lg font-semibold" style={{fontFamily:"'Work Sans', sans-serif"}}>Emails reçus / 7 jours</h3>
            </div>
            <div className="text-sm text-gray-500">{stats?.total_today ?? 0} aujourd'hui</div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats?.daily_volume || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#6B7280" }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #E5E7EB" }} />
                <Bar dataKey="count" fill="#002FA7" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-gray-200 p-6" data-testid="categories-widget">
          <div className="mb-4 pb-4 border-b border-gray-200">
            <div className="overline mb-1">// Classification IA</div>
            <h3 className="text-lg font-semibold" style={{fontFamily:"'Work Sans', sans-serif"}}>Catégories</h3>
          </div>
          <div className="space-y-2">
            {Object.entries(AI_CATEGORIES).map(([key, cat]) => {
              const n = stats?.categories?.[key] || 0;
              return (
                <div key={key} className="flex items-center justify-between py-1.5">
                  <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold border uppercase tracking-wider ${cat.className}`}>
                    {cat.label}
                  </span>
                  <span className="text-sm font-mono text-gray-700">{n}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Priority emails */}
      <div className="bg-white border border-gray-200">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <div className="overline mb-1">// Inbox</div>
            <h3 className="text-lg font-semibold" style={{fontFamily:"'Work Sans', sans-serif"}}>Emails prioritaires</h3>
          </div>
          <button onClick={() => nav("/emails")} className="text-sm text-[#002FA7] hover:underline font-medium flex items-center gap-1" data-testid="view-all-emails">
            Voir tout <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        {emails.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            <Inbox className="w-10 h-10 mx-auto mb-3 text-gray-300" strokeWidth={1.5} />
            <div className="text-sm">Aucun email prioritaire. Définissez des filtres ou lancez une sync.</div>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200" data-testid="priority-emails-list">
            {emails.map((em) => (
              <EmailRow key={em.id} email={em} onClick={() => nav("/emails")} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className={`bg-white border ${accent ? "border-[#002FA7]" : "border-gray-200"} p-5 transition-all hover:border-gray-900`} data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className="overline">{label}</div>
        <Icon className={`w-4 h-4 ${accent ? "text-[#002FA7]" : "text-gray-400"}`} strokeWidth={1.5} />
      </div>
      <div className="text-3xl font-bold tracking-tight" style={{fontFamily:"'Work Sans', sans-serif"}}>{value}</div>
    </div>
  );
}

function EmailRow({ email, onClick }) {
  const cat = AI_CATEGORIES[email.ai_category] || AI_CATEGORIES.administrative;
  return (
    <li onClick={onClick} className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors flex items-center gap-4">
      <span className={`inline-flex shrink-0 items-center px-2 py-0.5 text-xs font-semibold border uppercase tracking-wider ${cat.className}`}>
        {cat.label}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium text-gray-900 truncate">{email.from_name || email.from_email}</div>
          {!email.is_read && <div className="w-1.5 h-1.5 rounded-full bg-[#FF3B30]" />}
        </div>
        <div className="text-sm text-gray-700 truncate">{email.subject}</div>
        <div className="text-xs text-gray-500 truncate mt-0.5">{email.preview}</div>
      </div>
      <div className="text-xs text-gray-500 shrink-0">
        {new Date(email.received_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
      </div>
    </li>
  );
}
